import EnrollmentRecord from "./EnrollmentRecord";
import React, { useEffect, useRef, useState } from "react";
import EnrollmentTerms from "./EnrollmentTerms";
import {
  FaCamera,
  FaCheckCircle,
  FaShieldAlt,
  FaFilePdf,
  FaTimes,
} from "react-icons/fa";
import api from "../../api";
import { messageOf } from "./SignatureApi";

export default function SignatureEnrollment({ onClose, onApproved }) {
  const [showRecords, setShowRecords] = useState(false);
  const [birthDate, setBirthDate] = useState("");
  const [documentUrl, setDocumentUrl] = useState("");
  const [cameraKind, setCameraKind] = useState("selfie");
  const [phase, setPhase] = useState(0),
    [aiConsent, setAiConsent] = useState(false);
  const [data, setData] = useState(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const [photoDocumentFile, setPhotoDocumentFile] = useState(null);
  const [documentFile, setDocumentFile] = useState(null),
    [stage, setStage] = useState(null),
    [photo, setPhoto] = useState(null);
  const [camera, setCamera] = useState(false),
    [accepted, setAccepted] = useState(false),
    [photoUrl, setPhotoUrl] = useState("");
  const [cameraReady, setCameraReady] = useState(false);
  const video = useRef(null),
    stream = useRef(null),
    alive = useRef(true);
  const stop = () => {
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
    setCamera(false);
    setCameraReady(false);
  };
  const load = async () => {
    const next = (await api.get("/signatures/enrollment/status")).data;
    if (!alive.current) return;
    setData(next);
    setBirthDate(next.identity?.birthDate?.slice(0, 10) || "");
    if (
      next.enrollment?.status === "PENDING" &&
      next.enrollment.analysis &&
      next.enrollment.analysis.state !== "PROCESSING" &&
      next.enrollment.analysis.result !== "MATCH"
    )
      setPhase(1);
    if (
      next.enrollment?.status === "DRAFT" &&
      next.enrollment.currentPolicy &&
      Date.parse(next.enrollment.expiresAt) > Date.now()
    ) {
      setAccepted(Boolean(next.enrollment.hasAcceptance));
      setAiConsent(Boolean(next.policy.analysisEnabled));
      setPhase(
        next.enrollment.analysis?.result === "MATCH" &&
          !next.enrollment.photoDocumentRequired
          ? 2
          : 1,
      );
    }
    if (
      next.enrollment?.status === "DRAFT" &&
      next.enrollment.currentPolicy &&
      Date.parse(next.enrollment.expiresAt) > Date.now()
    )
      setStage({
        id: next.enrollment.id,
        nonce: next.enrollment.nonce,
        policyHash: next.policy.hash,
      });
    else setStage(null);
    return next;
  };
  useEffect(() => {
    if (
      data?.enrollment?.status !== "PENDING" ||
      !data?.policy.selfieQualityEnabled ||
      ["UNAVAILABLE", "COMPLETED"].includes(
        data?.enrollment?.selfieQuality?.state,
      )
    )
      return;
    const timer = setInterval(() => {
      load().catch(() => {});
    }, 2000);
    return () => clearInterval(timer);
  }, [
    data?.enrollment?.status,
    data?.policy.selfieQualityEnabled,
    data?.enrollment?.selfieQuality?.state,
  ]);
  useEffect(() => {
    alive.current = true;
    load().catch((e) => {
      if (alive.current)
        setError(
          e.name === "NotAllowedError"
            ? "Permita o acesso à câmera no navegador."
            : e.name === "NotFoundError"
              ? "Câmera não encontrada. Escolha um arquivo ou use outro dispositivo."
              : messageOf(e),
        );
    });
    return () => {
      alive.current = false;
      stream.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);
  useEffect(() => {
    if (!photo) {
      setPhotoUrl("");
      return;
    }
    const url = URL.createObjectURL(photo);
    setPhotoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);
  useEffect(() => {
    if (!documentFile) {
      setDocumentUrl("");
      return;
    }
    const url = URL.createObjectURL(documentFile);
    setDocumentUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [documentFile]);
  useEffect(() => {
    if (camera && video.current) video.current.srcObject = stream.current;
  }, [camera, cameraKind]);
  const perform = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (e) {
      const step = e.response?.data?.step;
      if (e.response?.data?.code === "TERMS_CHANGED") {
        await load().catch(() => {});
        setAccepted(false);
        setAiConsent(false);
      }
      setPhase((current) => (step === "selfie" || current === 3 ? 2 : current));
      if (step === "document" || step === "identity") {
        stop();
        setPhoto(null);
        setPhase(step === "identity" ? 0 : 1);
      }
      if (alive.current)
        setError(
          e.name === "NotAllowedError"
            ? "Permita o acesso à câmera no navegador."
            : e.name === "NotFoundError"
              ? "Câmera não encontrada. Escolha um arquivo ou use outro dispositivo."
              : messageOf(e),
        );
    } finally {
      if (alive.current) setBusy(false);
    }
  };
  const selectDocument = (file) => {
    setError("");
    if (!file) return;
    if (
      file.size > 10 * 1024 * 1024 ||
      !(
        /\.(pdf|jpe?g|png)$/i.test(file.name) ||
        ["application/pdf", "image/jpeg", "image/png"].includes(file.type)
      )
    ) {
      setError("Escolha PDF, JPG ou PNG de até 10 MB.");
      return;
    }
    stop();
    setDocumentFile(file);
  };
  const waitForAnalysis = async (initial) => {
    let analysis = initial;
    for (
      let attempt = 0;
      attempt < 65 && analysis?.state === "PROCESSING";
      attempt++
    ) {
      await new Promise((resolve) => setTimeout(resolve, 1800));
      if (!alive.current) return null;
      let status;
      try {
        status = await load();
      } catch (error) {
        if (attempt < 3 || !error.response || error.response.status >= 500)
          continue;
        throw new Error(
          "Seu documento foi recebido. Consulte a análise novamente em instantes.",
        );
      }
      analysis = status?.enrollment?.analysis;
    }
    if (analysis?.state === "PROCESSING")
      throw new Error(
        "A Nash IA continua analisando. Consulte o resultado em instantes.",
      );
    return analysis;
  };
  const sendDocument = () =>
    perform(async () => {
      if (!accepted || (data.policy.analysisEnabled && !aiConsent))
        throw new Error("Aceite os termos para enviar.");
      stop();
      if (
        stage &&
        data.enrollment?.analysis?.result === "MATCH" &&
        data.enrollment.photoDocumentRequired
      ) {
        if (!photoDocumentFile)
          throw new Error(
            "Anexe a frente do documento com foto no segundo campo.",
          );
        if (photoDocumentFile.size > 10 * 1024 * 1024)
          throw new Error("Use um arquivo de até 10 MB.");
        const extra = new FormData();
        extra.append("file", photoDocumentFile);
        await api.post(
          `/signatures/enrollment/${stage.id}/photo-document`,
          extra,
          { timeout: 60000 },
        );
        const updated = await load();
        if (updated.enrollment.photoDocumentRequired)
          throw new Error(
            updated.enrollment.supplementPortrait?.reason ||
              "Não foi possível confirmar a foto neste documento.",
          );
        setPhotoDocumentFile(null);
        setPhase(2);
        return;
      }
      if (!documentFile) {
        if (stage && data.enrollment?.analysis?.result === "MATCH") {
          setPhase(2);
          return;
        }
        throw new Error("Escolha ou fotografe seu documento.");
      }
      const body = new FormData();
      body.append("file", documentFile);
      body.append("accepted", "true");
      body.append("policyHash", data.policy.hash);
      body.append("termsHash", data.policy.termsHash);
      body.append("aiDocumentConsent", String(aiConsent));
      let result;
      try {
        result = (await api.post("/signatures/enrollment/document", body)).data;
      } catch (error) {
        if (
          error.response?.data?.enrollmentConflict !== "DIFFERENT_DOCUMENT" ||
          !(stage || data.enrollment?.status === "PENDING")
        )
          throw error;
        await api.delete("/signatures/enrollment");
        setStage(null);
        result = (await api.post("/signatures/enrollment/document", body)).data;
      }
      if (!alive.current) return;
      setStage(result);
      let analysis = result.analysis;
      if (data.policy.analysisEnabled) {
        analysis = await waitForAnalysis(analysis);
        await load();
      }
      if (data.policy.analysisEnabled && analysis?.result !== "MATCH") {
        setPhase(1);
        throw new Error(
          analysis?.reason ||
            (analysis?.result === "MISMATCH"
              ? "O documento não corresponde aos dados cadastrados."
              : "A Nash IA não concluiu a leitura. Repita a análise do documento."),
        );
      }
      setDocumentFile(null);
      const updated = data.policy.analysisEnabled ? await load() : null;
      setPhase(updated?.enrollment?.photoDocumentRequired ? 1 : 2);
    });
  const openCamera = (kind = "selfie") =>
    perform(async () => {
      if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia)
        throw new Error("Use HTTPS e um navegador com acesso à câmera.");
      if (kind === "selfie" && (!stage || !accepted))
        throw new Error("Envie o documento primeiro.");
      stop();
      setCameraKind(kind);
      if (kind === "selfie") {
        setPhase(2);
      }
      setCameraReady(false);
      const media = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: kind === "selfie" ? "user" : "environment" },
          width: { ideal: 1600 },
          height: { ideal: 1200 },
        },
        audio: false,
      });
      if (!alive.current) {
        media.getTracks().forEach((t) => t.stop());
        return;
      }
      stream.current = media;
      setCamera(true);
    });
  const capture = () =>
    perform(async () => {
      if (!cameraReady || !video.current?.videoWidth || !stream.current?.active)
        throw new Error("Aguarde a câmera carregar.");
      const canvas = document.createElement("canvas");
      canvas.width = video.current.videoWidth;
      canvas.height = video.current.videoHeight;
      canvas.getContext("2d").drawImage(video.current, 0, 0);
      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", 0.92),
      );
      if (!blob) throw new Error("Não foi possível capturar. Tente novamente.");
      if (cameraKind === "document")
        selectDocument(
          new File([blob], "documento-camera.jpg", { type: "image/jpeg" }),
        );
      else if (cameraKind === "photo-document")
        setPhotoDocumentFile(
          new File([blob], "frente-documento.jpg", { type: "image/jpeg" }),
        );
      else setPhoto(blob);
      stop();
    });
  const discard = () =>
    perform(async () => {
      stop();
      await api.delete("/signatures/enrollment");
      setPhoto(null);
      setDocumentFile(null);
      setAccepted(false);
      setStage(null);
      setAiConsent(false);
      await load();
      setPhase(1);
      window.dispatchEvent(new Event("signature-enrollment-changed"));
    });
  const submit = () =>
    perform(async () => {
      if (!stage || !photo || !accepted)
        throw new Error("Capture sua foto e aceite o termo para enviar.");
      const body = new FormData();
      body.append("file", photo, "captura-camera.jpg");
      body.append("nonce", stage.nonce);
      body.append("policyHash", stage.policyHash);
      body.append("accepted", "true");
      body.append("cameraCapture", "true");
      setPhase(3);
      await api.post(`/signatures/enrollment/${stage.id}/submit`, body);
      setPhoto(null);
      setStage(null);
      setDocumentFile(null);
      setAccepted(false);
      await load();
    });
  const downloadTerms = async (saved = false) =>
    perform(async () => {
      const response = await api.get(
        `/signatures/enrollment/terms.pdf${saved ? "?saved=1" : ""}`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = "termo-assinaturas-nash.pdf";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    });
  const row = data?.enrollment,
    approved = row?.status === "APPROVED" && row.currentPolicy;
  const pending =
    row?.status === "PENDING" &&
    row.currentPolicy &&
    (!row.analysis ||
      row.analysis.state === "PROCESSING" ||
      row.analysis.result === "MATCH") &&
    Date.parse(row.expiresAt) > Date.now();
  return (
    <section
      className="signature-enrollment"
      aria-label="Habilitar meu uso de assinaturas"
    >
      <header className="signature-toolbar">
        <div>
          <span className="signature-eyebrow">SUA IDENTIFICAÇÃO</span>
          <h2>Prepare sua conta</h2>
        </div>
        {onClose && (
          <button disabled={busy} onClick={onClose}>
            Fechar
          </button>
        )}
      </header>
      {error && (
        <p role="alert" className="signature-error">
          {error}
        </p>
      )}
      {!data && !error && <p role="status">Carregando identificação…</p>}
      {data?.policy.setupIssue && (
        <p role="alert" className="signature-error">
          {data.policy.setupIssue}
        </p>
      )}
      {data && (
        <>
          <ol
            className="signature-identity-stepper"
            aria-label="Etapas da habilitação"
          >
            {[
              "Identificação",
              "Documento",
              "Selfie",
              "Análise",
              "Conclusão",
            ].map((label, i) => (
              <li
                key={label}
                aria-current={
                  i ===
                  (approved || (pending && row.analysis?.state !== "PROCESSING")
                    ? 4
                    : pending
                      ? 3
                      : phase)
                    ? "step"
                    : undefined
                }
              >
                <span>{i + 1}</span>
                {label}
              </li>
            ))}
          </ol>
          {approved ? (
            <div className="signature-enrollment-status">
              <FaCheckCircle />
              <h3>Seu uso está habilitado</h3>

              <button onClick={onApproved || onClose}>Continuar</button>
              {row.imagesAvailable && (
                <>
                  <button onClick={() => setShowRecords((value) => !value)}>
                    {showRecords
                      ? "Ocultar meus registros"
                      : "Meus registros de identificação"}
                  </button>
                  {showRecords && <EnrollmentRecord record={row} />}
                </>
              )}
              {!row.imagesAvailable && (
                <p className="signature-help">
                  Os arquivos são removidos ao fim do prazo de retenção. Seu
                  aceite continua registrado.
                </p>
              )}
            </div>
          ) : pending ? (
            <div className="signature-enrollment-status">
              <FaShieldAlt />
              <h3>
                {data.policy.selfieQualityEnabled
                  ? row.selfieQuality?.state === "UNAVAILABLE" ||
                    row.selfieQuality?.result === "INCONCLUSIVE"
                    ? "Vamos conferir a mesma foto"
                    : "Analisando sua foto…"
                  : "Cadastro enviado"}
              </h3>
              {row.selfieQuality?.result === "INCONCLUSIVE" &&
                row.selfieQuality?.state !== "PROCESSING" && (
                  <p className="signature-help">
                    Sua foto foi mantida. Tente analisar novamente sem precisar
                    fotografar outra vez.
                  </p>
                )}
              {row.analysis && (
                <div
                  className={`signature-analysis-result ${row.analysis.result?.toLowerCase()}`}
                >
                  <strong>
                    {row.analysis.result === "MATCH"
                      ? "Dados do documento conferidos"
                      : row.analysis.result === "MISMATCH"
                        ? "Há divergências com o cadastro"
                        : "Análise documental inconclusiva"}
                  </strong>
                </div>
              )}
              {row.analysis?.checks && (
                <div className="signature-comparison-chips">
                  {[
                    ["Nome", "name"],
                    ["CPF", "cpf"],
                    ["Nascimento", "birthDate"],
                  ].map(([label, key]) => (
                    <span key={key}>
                      {label}:{" "}
                      {{
                        MATCH: "confere",
                        MISMATCH: "divergente",
                        UNREADABLE: "ilegível",
                        PROFILE_MISSING: "cadastro incompleto",
                        READING_CONFLICT: "leitura inconsistente",
                        NOT_REGISTERED: "não exigido",
                      }[row.analysis.checks[key]] || "pendente"}
                    </span>
                  ))}
                </div>
              )}
              <p className="signature-help">
                A liberação será automática quando as análises forem concluídas.
              </p>
              {row.analysis &&
                row.analysis.result !== "MATCH" &&
                row.analysis.state !== "PROCESSING" && (
                  <button disabled={busy} onClick={discard}>
                    Enviar outro documento
                  </button>
                )}
              <button disabled={busy} onClick={() => perform(load)}>
                Atualizar resultado
              </button>
              {data.policy.selfieQualityEnabled &&
                (row.selfieQuality?.state === "UNAVAILABLE" ||
                  row.selfieQuality?.result === "INCONCLUSIVE") && (
                  <button
                    disabled={busy}
                    onClick={() =>
                      perform(async () => {
                        await api.post(
                          `/signatures/enrollment/${row.id}/selfie/analyze`,
                        );
                        await load();
                      })
                    }
                  >
                    Tentar análise da foto novamente
                  </button>
                )}
              {row.analysis?.state === "UNAVAILABLE" && (
                <button
                  disabled={busy}
                  onClick={() =>
                    perform(async () => {
                      await api.post(
                        `/signatures/enrollment/${row.id}/analyze`,
                      );
                      await load();
                    })
                  }
                >
                  Repetir leitura documental
                </button>
              )}
            </div>
          ) : (
            <>
              {row && !row.currentPolicy && (
                <p className="signature-help">
                  O cadastro ou aviso foi alterado. Descarte a solicitação
                  anterior e inicie uma nova identificação.
                </p>
              )}
              {row?.status === "PENDING" &&
                row.analysis?.result !== "MATCH" && (
                  <p className="signature-error">
                    {row.analysis?.reason ||
                      "Confira o resultado da análise do documento."}
                  </p>
                )}
              {row?.note && ["REJECTED", "CORRECTION"].includes(row.status) && (
                <p className="signature-help">Última análise: {row.note}</p>
              )}
              {phase === 0 && (
                <section className="signature-card signature-identity-summary">
                  <h3>Confirme seus dados</h3>
                  <dl>
                    <dt>Nome</dt>
                    <dd>{data.identity?.name || "Não informado"}</dd>
                    <dt>CPF</dt>
                    <dd>{data.identity?.cpf || "Não informado"}</dd>
                  </dl>
                  <label>
                    Data de nascimento
                    <input
                      type="date"
                      value={birthDate}
                      min="1900-01-01"
                      max={new Date().toISOString().slice(0, 10)}
                      required
                      disabled={busy || Boolean(data.identity?.birthDate)}
                      onChange={(e) => setBirthDate(e.target.value)}
                    />
                  </label>

                  <button
                    className="signature-primary"
                    disabled={busy || !data.policy.hash || !birthDate}
                    onClick={() =>
                      perform(async () => {
                        await api.put("/signatures/enrollment/identity", {
                          birthDate,
                        });
                        await load();
                        setPhase(1);
                      })
                    }
                  >
                    Continuar
                  </button>
                </section>
              )}
              {phase === 1 && (
                <>
                  <section className="signature-card">
                    <h3>Documento com foto</h3>
                    <p className="signature-help">
                      RG, CIN ou CNH com foto. PDF, JPG ou PNG · até 10 MB e 2
                      páginas.
                    </p>
                    {stage && (
                      <p
                        className={
                          data.enrollment?.analysis?.result === "MATCH"
                            ? "signature-success"
                            : "signature-help"
                        }
                      >
                        {data.enrollment?.analysis?.result === "MATCH"
                          ? "Documento conferido."
                          : data.enrollment?.analysis?.state === "PROCESSING"
                            ? "Documento recebido. Analisando…"
                            : data.enrollment?.analysis?.state === "UNAVAILABLE"
                              ? "Documento recebido. Repita a leitura pela Nash IA."
                              : "Confira o resultado da análise."}
                      </p>
                    )}
                    <label className="signature-file-picker">
                      Escolher arquivo
                      <input
                        className="signature-document-file-input"
                        aria-label="Arquivo do documento"
                        type="file"
                        accept="application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png"
                        disabled={busy}
                        onChange={(e) => {
                          selectDocument(e.target.files?.[0]);
                          e.target.value = "";
                        }}
                      />
                    </label>
                    {documentFile && (
                      <figure className="signature-document-selected">
                        {documentUrl &&
                        !(
                          /\.pdf$/i.test(documentFile.name) ||
                          documentFile.type === "application/pdf"
                        ) ? (
                          <img src={documentUrl} alt="Documento selecionado" />
                        ) : (
                          <FaFilePdf aria-hidden="true" />
                        )}
                        <figcaption>
                          <strong>{documentFile.name}</strong>
                          <span>
                            {documentFile.size < 1024 * 1024
                              ? `${Math.max(1, Math.ceil(documentFile.size / 1024))} KB`
                              : `${(documentFile.size / 1024 / 1024).toFixed(1)} MB`}{" "}
                            · pronto para enviar
                          </span>
                        </figcaption>
                        <button
                          type="button"
                          aria-label="Remover documento"
                          disabled={busy}
                          onClick={() => setDocumentFile(null)}
                        >
                          <FaTimes />
                        </button>
                      </figure>
                    )}
                    {camera &&
                    ["document", "photo-document"].includes(cameraKind) ? (
                      <div className="signature-document-camera">
                        <video
                          ref={video}
                          autoPlay
                          playsInline
                          muted
                          aria-label="Câmera do documento"
                          onLoadedData={() => setCameraReady(true)}
                          onCanPlay={() => setCameraReady(true)}
                        />
                        <div className="signature-toolbar">
                          <button
                            disabled={busy || !cameraReady}
                            onClick={capture}
                          >
                            <FaCamera /> Capturar documento
                          </button>
                          <button disabled={busy} onClick={stop}>
                            Cancelar câmera
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => openCamera("document")}
                      >
                        <FaCamera /> Fotografar documento
                      </button>
                    )}
                    {data.enrollment?.photoDocumentRequired &&
                      data.enrollment?.analysis?.result === "MATCH" && (
                        <fieldset className="signature-card">
                          <legend>Documento com foto · frente</legend>
                          <p className="signature-help">
                            Os dados do primeiro arquivo estão conferidos. Anexe
                            também o lado com a foto.
                          </p>
                          <label className="signature-file-picker">
                            Anexar documento com foto
                            <input
                              aria-label="Documento complementar com foto"
                              type="file"
                              accept="application/pdf,image/jpeg,image/png"
                              disabled={busy}
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f?.size > 10 * 1024 * 1024)
                                  setError("Use um arquivo de até 10 MB.");
                                else {
                                  setError("");
                                  setPhotoDocumentFile(f || null);
                                }
                                e.target.value = "";
                              }}
                            />
                          </label>
                          {photoDocumentFile && (
                            <p>
                              {photoDocumentFile.name}
                              <button
                                type="button"
                                disabled={busy}
                                aria-label="Remover documento complementar"
                                onClick={() => setPhotoDocumentFile(null)}
                              >
                                <FaTimes />
                              </button>
                            </p>
                          )}
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => openCamera("photo-document")}
                          >
                            <FaCamera /> Fotografar frente com foto
                          </button>
                          {data.enrollment?.supplementPortrait?.reason && (
                            <p role="status">
                              {data.enrollment.supplementPortrait.reason}
                            </p>
                          )}
                        </fieldset>
                      )}
                    {stage &&
                      !error &&
                      data.enrollment?.analysis?.reason &&
                      data.enrollment?.analysis?.result !== "MATCH" && (
                        <p className="signature-help">
                          {data.enrollment.analysis.reason}
                        </p>
                      )}
                    {stage &&
                      data.enrollment?.analysis?.issues?.length > 0 &&
                      data.enrollment.analysis.state !== "PROCESSING" && (
                        <div
                          className="signature-reading-issues"
                          aria-label="Resultado por campo"
                        >
                          {data.enrollment.analysis.issues.map((issue) => (
                            <p key={issue.field}>
                              <strong>{issue.label}</strong>
                              <span>
                                {issue.message.replace(`${issue.label}: `, "")}
                              </span>
                            </p>
                          ))}
                        </div>
                      )}
                    {stage &&
                      data.enrollment?.analysis?.extracted &&
                      data.enrollment.analysis.result !== "MATCH" && (
                        <details className="signature-card">
                          <summary>Conferir dados lidos</summary>
                          <div className="signature-analysis-table">
                            <table>
                              <thead>
                                <tr>
                                  <th>Campo</th>
                                  <th>Minha conta</th>
                                  <th>Documento</th>
                                  <th>Resultado</th>
                                </tr>
                              </thead>
                              <tbody>
                                {[
                                  ["Nome", "name"],
                                  ["CPF", "cpf"],
                                  ["Nascimento", "birthDate"],
                                ].map(([label, key]) => {
                                  const format = (value) => {
                                    if (!value) return "Não lido";
                                    const text = String(value);
                                    return key === "birthDate" &&
                                      /^\d{4}-\d{2}-\d{2}$/.test(text)
                                      ? text.split("-").reverse().join("/")
                                      : key === "cpf" &&
                                          /^\d{11}$/.test(
                                            text.replace(/\D/g, ""),
                                          )
                                        ? text
                                            .replace(/\D/g, "")
                                            .replace(
                                              /(\d{3})(\d{3})(\d{3})(\d{2})/,
                                              "$1.$2.$3-$4",
                                            )
                                        : text;
                                  };
                                  return (
                                    <tr key={key}>
                                      <td>{label}</td>
                                      <td>{format(data.identity?.[key])}</td>
                                      <td>
                                        {format(
                                          data.enrollment.analysis.extracted[
                                            key
                                          ],
                                        )}
                                      </td>
                                      <td>
                                        {{
                                          MATCH: "Confere",
                                          MISMATCH: "Diferente do cadastro",
                                          UNREADABLE: "Leitura não concluída",
                                          READING_CONFLICT:
                                            "Leituras sem consenso",
                                          PROFILE_MISSING: "Confira sua conta",
                                          NOT_REGISTERED: "Não exigido",
                                        }[
                                          data.enrollment.analysis.checks?.[key]
                                        ] || "Pendente"}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                          {Object.values(
                            data.enrollment.analysis.checks || {},
                          ).includes("READING_CONFLICT") && (
                            <p className="signature-help">
                              O valor exibido é uma transcrição provisória. Mesmo
                              quando coincide com sua conta, a análise precisa de
                              leituras independentes convergentes para liberar
                              a habilitação. Você pode repetir a leitura do
                              documento sem enviar outro arquivo.
                            </p>
                          )}
                        </details>
                      )}
                    {stage &&
                      data.enrollment?.analysis?.state === "PROCESSING" && (
                        <div>
                          <p role="status">
                            A Nash IA está analisando seu documento…
                          </p>
                          <button
                            disabled={busy}
                            onClick={() =>
                              perform(async () => {
                                await waitForAnalysis(data.enrollment.analysis);
                                await load();
                              })
                            }
                          >
                            Consultar análise
                          </button>
                        </div>
                      )}
                    {stage &&
                      data.enrollment?.analysis?.state !== "PROCESSING" &&
                      data.enrollment?.analysis?.result !== "MATCH" && (
                        <button
                          disabled={busy}
                          onClick={() =>
                            perform(async () => {
                              const response = await api.post(
                                `/signatures/enrollment/${stage.id}/analyze`,
                              );
                              await waitForAnalysis(
                                response.data.enrollment?.analysis,
                              );
                              await load();
                            })
                          }
                        >
                          Repetir leitura documental
                        </button>
                      )}
                  </section>
                  <section className="signature-card">
                    <details className="signature-enrollment-terms">
                      <summary>Termos e privacidade</summary>
                      <EnrollmentTerms text={data.policy.terms} />
                      <p className="signature-help">
                        Retenção: até {data.policy.retentionDays} dias. Contato:{" "}
                        {data.policy.privacyContact}.
                      </p>
                    </details>
                    <button disabled={busy} onClick={() => downloadTerms()}>
                      Baixar termo
                    </button>
                    <label className="signature-check">
                      <input
                        type="checkbox"
                        checked={accepted}
                        disabled={busy}
                        onChange={(e) => setAccepted(e.target.checked)}
                      />
                      Li e aceito os termos.
                    </label>
                    {data.policy.analysisEnabled && (
                      <label className="signature-check">
                        <input
                          type="checkbox"
                          checked={aiConsent}
                          disabled={busy}
                          onChange={(e) => setAiConsent(e.target.checked)}
                        />
                        Autorizo a Nash IA a ler os dados do documento e avaliar
                        a qualidade da foto.
                      </label>
                    )}
                  </section>
                  <button
                    className="signature-primary"
                    disabled={
                      busy ||
                      data.enrollment?.analysis?.state === "PROCESSING" ||
                      !accepted ||
                      (data.policy.analysisEnabled && !aiConsent) ||
                      (!stage && !documentFile)
                    }
                    onClick={sendDocument}
                  >
                    {busy ? "Conferindo…" : "Enviar documento"}
                  </button>
                </>
              )}
              {phase >= 2 && (
                <section className="signature-card">
                  <h3>{phase === 3 && busy ? "Analisando…" : "Sua selfie"}</h3>
                  <p className="signature-help">
                    Olhe para a câmera com o rosto visível. Não precisa encaixar
                    perfeitamente.
                  </p>
                  {row?.selfieQuality?.result === "RETAKE" && (
                    <p role="alert" className="signature-error">
                      {row.selfieQuality.reason}
                    </p>
                  )}
                  {camera && cameraKind === "selfie" && (
                    <div className="signature-selfie-preview">
                      <video
                        ref={video}
                        autoPlay
                        playsInline
                        muted
                        aria-label="Câmera ao vivo"
                        onLoadedData={() => setCameraReady(true)}
                        onCanPlay={() => setCameraReady(true)}
                      />
                      <span className="signature-selfie-caption" role="status">
                        {cameraReady
                          ? "Tudo pronto para fotografar"
                          : "Preparando câmera…"}
                      </span>
                    </div>
                  )}
                  {photoUrl && !camera && (
                    <div className="signature-selfie-preview">
                      <img src={photoUrl} alt="Foto capturada nesta sessão" />
                      <span className="signature-selfie-caption">
                        Sua foto está pronta para enviar
                      </span>
                    </div>
                  )}
                  <div className="signature-toolbar">
                    {camera ? (
                      <button disabled={busy || !cameraReady} onClick={capture}>
                        <FaCamera /> Capturar foto
                      </button>
                    ) : (
                      <button
                        disabled={busy}
                        onClick={() => openCamera("selfie")}
                      >
                        {photo ? "Tirar outra foto" : "Abrir câmera"}
                      </button>
                    )}
                    {camera && (
                      <button disabled={busy} onClick={stop}>
                        Parar câmera
                      </button>
                    )}
                  </div>
                  <button
                    className="signature-primary"
                    disabled={busy || camera || !photo || !accepted || !stage}
                    onClick={submit}
                  >
                    {busy ? "Enviando…" : "Enviar selfie"}
                  </button>
                </section>
              )}
            </>
          )}
          {row?.hasAcceptance && (
            <button disabled={busy} onClick={() => downloadTerms(true)}>
              Baixar meu aceite registrado
            </button>
          )}
          {(stage ||
            ["DRAFT", "PENDING", "APPROVED"].includes(row?.status)) && (
            <button disabled={busy} onClick={discard}>
              {row?.status === "APPROVED"
                ? "Desabilitar meu uso futuro"
                : "Descartar cadastro e imagens"}
            </button>
          )}
        </>
      )}
    </section>
  );
}
